import { Canvas, TEvent } from 'fabric';
import { SketchProperties, SketchState } from '../../types';
import { AbstractEventAware, FabricCanvasTool } from './tools';
import type SketchField from './SketchField';

export interface EventInput {
  event: TEvent;
  tool: FabricCanvasTool;
  props: SketchProperties;
  state: SketchState;
}
export interface EventCallback {
  (input: EventInput): void;
}

/**
 * Canvas Events Manager Class
 */
export class FabricCanvasEventsManager extends AbstractEventAware {
    private canvas: Canvas;

    private sketch: SketchField;

    constructor(
        canvas: Canvas,
        sketch: SketchField,
    ) {
        super();
        this.canvas = canvas;
        this.sketch = sketch;
    }

    addToHistory(event, state, modified = false) {
        if (!state.action) {
            this.sketch.setState({ action: true });
            return;
        }
        // @ts-ignore
        const obj = event.target;
        obj.__version = modified ? obj.__version + 1 : 1;
        // record current object state as json and save as originalState
        const objState = obj.toJSON();
        obj.__originalState = objState;
        const stringState = JSON.stringify(objState);
        // object, previous state, current state
        this.sketch.history.keep([obj, stringState, stringState]);
        this.sketch.props.onChange!();
    }

    /**
   * Action when an object is added to the canvas
   */
    public onObjectAdded({
        event, tool, props, state, 
    }: EventInput): void {
        this.addToHistory(event, state);
    }

    public onObjectMoving() {
        this.sketch?.props?.onChange!();
    }

    public onObjectModified({
        event, tool, props, state,
    }: EventInput) {
        this.addToHistory(event, state, true);
    }

    public onObjectRemoved() {
        this.sketch?.props?.onChange!();
    }

    public onObjectScaling() {
        this.sketch?.props?.onChange!();
    }

    public onObjectRotating() {
        this.sketch?.props?.onChange!();
    }

    public onMouseUp({
        event, tool, props, state, 
    }: EventInput): void {
        tool.doMouseUp(event);
    }

    public onMouseDown({
        event, tool, props, state, 
    }: EventInput): void {
        tool.doMouseDown(event);
    }

    public onMouseMove({
        event, tool, props, state, 
    }: EventInput): void {
        tool.doMouseMove(event);
    }

    public onMouseOut({
        event, tool, props, state, 
    }: EventInput): void {
        tool.doMouseOut(event);
    }

    public onSelectionCreated(input: EventInput) {
        super.onSelectionCreated(input);
        this.sketch.hasSelection = true;
        this.sketch.props.onChange!();
    }

    public onSelectionChanged(input: EventInput) {
        super.onSelectionChanged(input);
        this.sketch.hasSelection = true;
        this.sketch.props.onChange!();
    }

    public onSelectionCleared(input: EventInput) {
        super.onSelectionCleared(input);
        this.sketch.hasSelection = false;
        this.sketch.props.onChange!();
    }
}
